import { startOfDay } from 'date-fns';
import { asTimezone } from '@lgc/database/types/domains/club';
import { toZonedTime } from 'date-fns-tz';

// src/subscription/constants.ts
var ACTIVE_SESSION_OVERAGE_ENABLED = true;
var INACTIVE_AFTER_DAYS = 60;

// src/subscription/types.ts
function isMembershipBooker(subscription) {
  return subscription.type === "membership";
}
function isPassBooker(subscription) {
  return subscription.type === "pass";
}
function isNoSubscription(subscription) {
  return subscription.type === "none";
}
function hasActivePlan(subscription) {
  return subscription.type !== "none";
}

// src/datetime/branded.ts
function asZonedDate(d) {
  return d;
}

// src/datetime/errors.ts
var DateTimeLibraryError = class extends Error {
  constructor(type, message, details) {
    super(message);
    this.name = "DateTimeLibraryError";
    this.type = type;
    this.details = details;
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, this.constructor);
    }
  }
};
var InvalidTimezoneError = class extends DateTimeLibraryError {
  constructor(message) {
    super("INVALID_TIMEZONE", message);
    this.name = "InvalidTimezoneError";
  }
};
var InvalidDateError = class extends DateTimeLibraryError {
  constructor(message) {
    super("INVALID_DATE", message);
    this.name = "InvalidDateError";
  }
};

// src/datetime/constants.ts
var SUPPORTED_TIMEZONES = [
  // Fallback (not a picker option — see note above)
  "UTC",
  // US
  "America/New_York",
  "America/Chicago",
  "America/Denver",
  "America/Los_Angeles",
  "America/Anchorage",
  "Pacific/Honolulu",
  "America/Phoenix",
  // Canada
  "America/Toronto",
  "America/Vancouver",
  "America/Halifax",
  "America/Winnipeg",
  "America/Edmonton",
  // Mexico
  "America/Mexico_City",
  "America/Cancun",
  "America/Tijuana"
];
asTimezone("America/New_York");

// src/datetime/converters.ts
function validateDate(date) {
  if (!(date instanceof Date) || isNaN(date.getTime())) {
    throw new InvalidDateError("Invalid date provided");
  }
}
function validateTimezone(timezone) {
  if (!timezone || typeof timezone !== "string") {
    throw new InvalidTimezoneError("Timezone must be a non-empty string");
  }
  if (!SUPPORTED_TIMEZONES.includes(timezone)) {
    throw new InvalidTimezoneError(
      `Unsupported timezone: ${timezone}. Must be one of: ${SUPPORTED_TIMEZONES.join(", ")}`
    );
  }
}
function utcToClubTime(utcDate, timezone) {
  validateDate(utcDate);
  validateTimezone(timezone);
  return asZonedDate(toZonedTime(utcDate, timezone));
}

// src/shared/hours.ts
function hoursLabel(hours) {
  return `${hours} hour${hours === 1 ? "" : "s"}`;
}
function roundHours(minutes, decimals = 1) {
  const scale = 10 ** decimals;
  return Math.round(minutes * scale / 60) / scale;
}
function formatHoursDisplay(minutes, options = {}) {
  const { decimals = 1, unit = "h", trimZeros = true } = options;
  const hours = roundHours(minutes, decimals);
  if (unit === " hours") return hoursLabel(hours);
  return `${trimZeros ? hours : hours.toFixed(decimals)}${unit}`;
}

// src/subscription/checkers.ts
var hoursText = (minutes) => formatHoursDisplay(minutes, { unit: "", trimZeros: false });
function checkDailyMinuteLimit(requestedDurationMinutes, minutesUsedToday, maxMinutesPerDay) {
  if (maxMinutesPerDay === null) {
    return { isAllowed: true };
  }
  const totalMinutes = minutesUsedToday + requestedDurationMinutes;
  const remainingMinutes = Math.max(0, maxMinutesPerDay - minutesUsedToday);
  if (totalMinutes > maxMinutesPerDay) {
    const hoursUsed = hoursText(minutesUsedToday);
    const hoursRequested = hoursText(requestedDurationMinutes);
    const maxHours = hoursText(maxMinutesPerDay);
    const remainingHours = hoursText(remainingMinutes);
    return {
      isAllowed: false,
      reason: `Daily minute limit exceeded. You have ${hoursUsed} hours (${minutesUsedToday} min) booked today, this booking would add ${hoursRequested} hours (${requestedDurationMinutes} min) (max: ${maxHours} hours/day). Remaining: ${remainingHours} hours (${remainingMinutes} min)`,
      constraint: "maxMinutesPerDay",
      remaining: remainingMinutes,
      suggestion: remainingMinutes > 0 ? `Try booking for ${remainingMinutes} minutes or less` : "You have reached your daily limit. Try booking tomorrow."
    };
  }
  return {
    isAllowed: true,
    remaining: remainingMinutes
  };
}
function checkWeeklyMinuteLimit(requestedDurationMinutes, minutesUsedThisWeek, maxMinutesPerWeek) {
  if (maxMinutesPerWeek === null) {
    return { isAllowed: true };
  }
  const totalMinutes = minutesUsedThisWeek + requestedDurationMinutes;
  const remainingMinutes = Math.max(0, maxMinutesPerWeek - minutesUsedThisWeek);
  if (totalMinutes > maxMinutesPerWeek) {
    const hoursUsed = hoursText(minutesUsedThisWeek);
    const hoursRequested = hoursText(requestedDurationMinutes);
    const maxHours = hoursText(maxMinutesPerWeek);
    const remainingHours = hoursText(remainingMinutes);
    return {
      isAllowed: false,
      reason: `Weekly minute limit exceeded. You have ${hoursUsed} hours (${minutesUsedThisWeek} min) booked this week, this booking would add ${hoursRequested} hours (${requestedDurationMinutes} min) (max: ${maxHours} hours/week). Remaining: ${remainingHours} hours (${remainingMinutes} min)`,
      constraint: "maxMinutesPerWeek",
      remaining: remainingMinutes,
      suggestion: remainingMinutes > 0 ? `Try booking for ${remainingMinutes} minutes or less` : "You have reached your weekly limit. Your hours will reset at the start of next week."
    };
  }
  return {
    isAllowed: true,
    remaining: remainingMinutes
  };
}
function checkMonthlyMinuteLimit(requestedDurationMinutes, minutesUsedThisMonth, maxMinutesPerMonth) {
  if (maxMinutesPerMonth === null) {
    return { isAllowed: true };
  }
  const totalMinutes = minutesUsedThisMonth + requestedDurationMinutes;
  const remainingMinutes = Math.max(0, maxMinutesPerMonth - minutesUsedThisMonth);
  if (totalMinutes > maxMinutesPerMonth) {
    const hoursUsed = hoursText(minutesUsedThisMonth);
    const hoursRequested = hoursText(requestedDurationMinutes);
    const maxHours = hoursText(maxMinutesPerMonth);
    const remainingHours = hoursText(remainingMinutes);
    return {
      isAllowed: false,
      reason: `Monthly minute limit exceeded. You have ${hoursUsed} hours (${minutesUsedThisMonth} min) booked this billing cycle, this booking would add ${hoursRequested} hours (${requestedDurationMinutes} min) (max: ${maxHours} hours/month). Remaining: ${remainingHours} hours (${remainingMinutes} min)`,
      constraint: "maxMinutesPerMonth",
      remaining: remainingMinutes,
      suggestion: remainingMinutes > 0 ? `Try booking for ${remainingMinutes} minutes or less` : "You have reached your monthly limit. Your hours will reset at the start of your next billing cycle."
    };
  }
  return {
    isAllowed: true,
    remaining: remainingMinutes
  };
}
function checkActiveMinutesLimit(activeBookingsTotalMinutes, requestedDurationMinutes, maxActiveMinutes) {
  if (maxActiveMinutes === null) {
    return { isAllowed: true };
  }
  const totalActiveMinutes = activeBookingsTotalMinutes + requestedDurationMinutes;
  const remainingMinutes = Math.max(0, maxActiveMinutes - activeBookingsTotalMinutes);
  if (totalActiveMinutes > maxActiveMinutes) {
    const hoursActive = hoursText(activeBookingsTotalMinutes);
    const hoursRequested = hoursText(requestedDurationMinutes);
    const maxHours = hoursText(maxActiveMinutes);
    const remainingHours = hoursText(remainingMinutes);
    return {
      isAllowed: false,
      reason: `Active minutes limit exceeded. You currently have ${hoursActive} hours (${activeBookingsTotalMinutes} min) of upcoming bookings, this booking would add ${hoursRequested} hours (${requestedDurationMinutes} min) (max: ${maxHours} hours). Remaining: ${remainingHours} hours (${remainingMinutes} min)`,
      constraint: "maxActiveMinutes",
      remaining: remainingMinutes,
      suggestion: remainingMinutes > 0 ? `Try booking for ${remainingMinutes} minutes or less` : "Cancel or complete an existing booking before scheduling more hours."
    };
  }
  return {
    isAllowed: true,
    remaining: remainingMinutes
  };
}
function checkPassRemainingMinutes(passConstraints) {
  const remaining = passConstraints.remainingMinutes;
  const total = passConstraints.maxTotalMinutes;
  if (remaining === null || total === null) {
    return {
      isAllowed: true
    };
  }
  if (remaining <= 0) {
    const hoursTotal = hoursText(total);
    return {
      isAllowed: false,
      reason: `Pass "${passConstraints.passName}" is fully utilized. You have used all ${hoursTotal} hours.`,
      constraint: "remainingMinutes",
      remaining: 0,
      suggestion: "Purchase a new pass to continue booking."
    };
  }
  const usagePercent = (total - remaining) / total * 100;
  const hoursRemaining = hoursText(remaining);
  const suggestion = usagePercent >= 80 ? `Only ${hoursRemaining} hours remaining on your pass. Consider purchasing another pass.` : void 0;
  return {
    isAllowed: true,
    remaining,
    suggestion
  };
}
function isWithinCustomBookingHours(bookingSlot, customSchedule, timezone, bookingDate, subscriptionLabel = "membership") {
  const clubDate = utcToClubTime(bookingDate, timezone);
  const dayOfWeek = clubDate.getDay();
  const dayNames = ["sunday", "monday", "tuesday", "wednesday", "thursday", "friday", "saturday"];
  const dayName = dayNames[dayOfWeek];
  const daySchedule = customSchedule[dayName];
  if (!daySchedule || daySchedule.length === 0) {
    return {
      isWithin: false,
      reason: `Your ${subscriptionLabel} does not allow bookings on ${dayName}s`
    };
  }
  const bookingStartMinutes = bookingSlot.startMinutes;
  const bookingEndMinutes = bookingSlot.startMinutes + bookingSlot.durationMinutes;
  for (const segment of daySchedule) {
    const allowedStartMinutes = segment.startMinutes;
    const allowedEndMinutes = segment.endMinutes;
    const isOvernightSegment = allowedEndMinutes > 1440;
    if (isOvernightSegment) {
      const adjustedEndMinutes = allowedEndMinutes - 1440;
      const bookingStartValid = bookingStartMinutes >= allowedStartMinutes || bookingStartMinutes < adjustedEndMinutes;
      const bookingEndValid = bookingEndMinutes > allowedStartMinutes || bookingEndMinutes <= adjustedEndMinutes;
      if (bookingStartValid && bookingEndValid) {
        return { isWithin: true };
      }
    } else {
      if (bookingStartMinutes >= allowedStartMinutes && bookingEndMinutes <= allowedEndMinutes) {
        return { isWithin: true };
      }
    }
  }
  const formatTime = (minutes) => {
    const adjustedMinutes = minutes > 1440 ? minutes - 1440 : minutes;
    const hour = Math.floor(adjustedMinutes / 60);
    const minute = adjustedMinutes % 60;
    const period = hour >= 12 ? "PM" : "AM";
    const displayHour = hour === 0 ? 12 : hour > 12 ? hour - 12 : hour;
    return `${displayHour}:${minute.toString().padStart(2, "0")} ${period}`;
  };
  const allowedHours = daySchedule.map(
    (segment) => `${formatTime(segment.startMinutes)}\u2013${formatTime(segment.endMinutes)}`
  ).join(" and ");
  return {
    isWithin: false,
    reason: `Your ${subscriptionLabel} only allows bookings between ${allowedHours} on ${dayName}s`
  };
}
function checkDailyVisitLimit(bookingsToday, maxVisitsPerDay) {
  if (maxVisitsPerDay === null) {
    return { isAllowed: true };
  }
  const totalVisits = bookingsToday + 1;
  const remainingVisits = Math.max(0, maxVisitsPerDay - bookingsToday);
  if (totalVisits > maxVisitsPerDay) {
    return {
      isAllowed: false,
      reason: `Daily visit limit exceeded. You have ${bookingsToday} booking${bookingsToday === 1 ? "" : "s"} today (max: ${maxVisitsPerDay} visit${maxVisitsPerDay === 1 ? "" : "s"}/day). Remaining: ${remainingVisits} visit${remainingVisits === 1 ? "" : "s"}`,
      constraint: "maxVisitsPerDay",
      remaining: remainingVisits,
      suggestion: remainingVisits === 0 ? "You have reached your daily visit limit. Try booking tomorrow." : void 0
    };
  }
  return {
    isAllowed: true,
    remaining: remainingVisits
  };
}
function checkActiveBookingLimit(activeBookings, maxActiveBookings) {
  if (maxActiveBookings === null) {
    return { isAllowed: true };
  }
  const totalBookings = activeBookings + 1;
  const remainingSlots = Math.max(0, maxActiveBookings - activeBookings);
  if (totalBookings > maxActiveBookings) {
    return {
      isAllowed: false,
      reason: `Active booking limit exceeded. You have ${activeBookings} active booking${activeBookings === 1 ? "" : "s"} (max: ${maxActiveBookings} active booking${maxActiveBookings === 1 ? "" : "s"}). Remaining: ${remainingSlots} slot${remainingSlots === 1 ? "" : "s"}`,
      constraint: "maxActiveSessions",
      remaining: remainingSlots,
      suggestion: remainingSlots === 0 ? "Cancel an existing booking to make a new one, or wait for a booking to complete." : void 0
    };
  }
  const suggestion = remainingSlots === 1 ? "This is your last available booking slot." : void 0;
  return {
    isAllowed: true,
    remaining: remainingSlots,
    suggestion
  };
}

// src/subscription/validators.ts
var hoursText2 = (minutes) => formatHoursDisplay(minutes, { unit: "", trimZeros: false });
function isSubscriptionValid(subscription, now = /* @__PURE__ */ new Date()) {
  const errors = [];
  const warnings = [];
  const constraints = {};
  if (subscription.type === "none") {
    return { isValid: true, canBook: true, errors, warnings, constraints };
  }
  if (subscription.type === "membership") {
    if (subscription.ended) {
      const endedDate = subscription.endedAt ? subscription.endedAt.toLocaleDateString() : "unknown date";
      errors.push(`Membership "${subscription.membershipName}" has been terminated on ${endedDate}`);
      return { isValid: false, canBook: false, errors, warnings, constraints };
    }
    if (subscription.endDate && subscription.endDate < now) {
      errors.push(`Membership "${subscription.membershipName}" has expired on ${subscription.endDate.toLocaleDateString()}`);
      return { isValid: false, canBook: false, errors, warnings, constraints };
    }
    return { isValid: true, canBook: true, errors, warnings, constraints };
  }
  if (subscription.type === "pass") {
    if (subscription.status !== "active") {
      errors.push(`Pass "${subscription.passName}" is ${subscription.status}`);
      return { isValid: false, canBook: false, errors, warnings, constraints };
    }
    if (subscription.expirationDate !== null && subscription.expirationDate < now) {
      errors.push(`Pass "${subscription.passName}" has expired on ${subscription.expirationDate.toLocaleDateString()}`);
      return { isValid: false, canBook: false, errors, warnings, constraints };
    }
    if (subscription.remainingMinutes !== null && subscription.remainingMinutes <= 0) {
      const hoursTotal = subscription.maxTotalMinutes ? hoursText2(subscription.maxTotalMinutes) : "unknown";
      errors.push(`Pass "${subscription.passName}" is fully utilized. No remaining minutes (${hoursTotal} hours total).`);
      return { isValid: false, canBook: false, errors, warnings, constraints };
    }
    return { isValid: true, canBook: true, errors, warnings, constraints };
  }
  return { isValid: true, canBook: true, errors, warnings, constraints };
}
function canBookAtTime(subscription, requestedSlot, usageSummary, timezone, bookingDate) {
  const validityCheck = isSubscriptionValid(subscription);
  if (!validityCheck.isValid) {
    return {
      isAllowed: false,
      reason: validityCheck.errors[0] || "Subscription is not valid",
      constraint: "subscription-expired"
    };
  }
  if (subscription.type === "none") {
    return { isAllowed: true };
  }
  const fullValidation = validateSubscriptionForBooking(
    subscription,
    requestedSlot,
    usageSummary,
    timezone,
    bookingDate
  );
  if (!fullValidation.canBook) {
    return {
      isAllowed: false,
      reason: fullValidation.errors[0] || "Booking not allowed",
      constraint: "constraint-violation"
    };
  }
  return { isAllowed: true };
}
function validateSubscriptionForBooking(subscription, requestedSlot, usageSummary, timezone, bookingDate) {
  const errors = [];
  const warnings = [];
  const constraints = {};
  if (subscription.type === "none") {
    return { isValid: true, canBook: true, errors, warnings, constraints };
  }
  const validityCheck = isSubscriptionValid(subscription, bookingDate);
  if (!validityCheck.isValid) {
    return validityCheck;
  }
  const endDate = new Date(bookingDate);
  endDate.setMinutes(endDate.getMinutes() + requestedSlot.durationMinutes);
  if (subscription.type === "membership" && subscription.endDate) {
    if (subscription.endDate < endDate) {
      warnings.push(
        `Membership "${subscription.membershipName}" will expire on ${subscription.endDate.toLocaleDateString()} before this booking ends`
      );
    }
  } else if (subscription.type === "pass") {
    if (subscription.expirationDate !== null && subscription.expirationDate < endDate) {
      warnings.push(
        `Pass "${subscription.passName}" will expire on ${subscription.expirationDate.toLocaleDateString()} before this booking ends`
      );
    }
  }
  if (subscription.type === "membership") {
    return validateMembershipConstraints(
      subscription,
      requestedSlot,
      usageSummary,
      timezone,
      bookingDate,
      errors,
      warnings,
      constraints
    );
  }
  if (subscription.type === "pass") {
    return validatePassConstraints(
      subscription,
      requestedSlot,
      usageSummary,
      timezone,
      bookingDate,
      errors,
      warnings,
      constraints
    );
  }
  return { isValid: true, canBook: true, errors, warnings, constraints };
}
function validateMembershipConstraints(membership, requestedSlot, usageSummary, timezone, bookingDate, errors, warnings, constraints) {
  const bookingDurationMinutes = requestedSlot.durationMinutes;
  if (membership.maxMinutesPerDay !== null) {
    const totalMinutesToday = usageSummary.minutesUsedToday + bookingDurationMinutes;
    const remainingMinutes = Math.max(0, membership.maxMinutesPerDay - usageSummary.minutesUsedToday);
    if (totalMinutesToday > membership.maxMinutesPerDay) {
      const bookingDateStr = startOfDay(bookingDate).toLocaleDateString();
      const remainingHours = hoursText2(remainingMinutes);
      const errorMsg = `Maximum minutes per day (${membership.maxMinutesPerDay} min = ${hoursText2(membership.maxMinutesPerDay)}h) exceeded. You have ${usageSummary.minutesUsedToday} minutes booked on ${bookingDateStr}, this booking would add ${bookingDurationMinutes} minutes. Maximum remaining: ${remainingHours} hours`;
      errors.push(errorMsg);
      constraints.minutesPerDay = {
        isAllowed: false,
        reason: errorMsg,
        constraint: "maxMinutesPerDay",
        remaining: remainingMinutes
      };
    } else {
      constraints.minutesPerDay = {
        isAllowed: true,
        remaining: remainingMinutes
      };
    }
  }
  if (membership.maxMinutesPerWeek !== null) {
    const totalMinutesThisWeek = usageSummary.minutesUsedThisWeek + bookingDurationMinutes;
    const remainingMinutes = Math.max(0, membership.maxMinutesPerWeek - usageSummary.minutesUsedThisWeek);
    if (totalMinutesThisWeek > membership.maxMinutesPerWeek) {
      const remainingHours = hoursText2(remainingMinutes);
      const errorMsg = `Maximum minutes per week (${membership.maxMinutesPerWeek} min = ${hoursText2(membership.maxMinutesPerWeek)}h) exceeded. You have ${usageSummary.minutesUsedThisWeek} minutes booked this week, this booking would add ${bookingDurationMinutes} minutes. Maximum remaining: ${remainingHours} hours`;
      errors.push(errorMsg);
      constraints.minutesPerWeek = {
        isAllowed: false,
        reason: errorMsg,
        constraint: "maxMinutesPerWeek",
        remaining: remainingMinutes
      };
    } else {
      constraints.minutesPerWeek = {
        isAllowed: true,
        remaining: remainingMinutes
      };
    }
  }
  if (membership.maxMinutesPerMonth !== null) {
    const totalMinutesThisMonth = usageSummary.minutesUsedThisMonth + bookingDurationMinutes;
    const remainingMinutes = Math.max(0, membership.maxMinutesPerMonth - usageSummary.minutesUsedThisMonth);
    if (totalMinutesThisMonth > membership.maxMinutesPerMonth) {
      const remainingHours = hoursText2(remainingMinutes);
      const errorMsg = `Maximum minutes per billing cycle (${membership.maxMinutesPerMonth} min = ${hoursText2(membership.maxMinutesPerMonth)}h) exceeded. You have ${usageSummary.minutesUsedThisMonth} minutes booked this billing cycle, this booking would add ${bookingDurationMinutes} minutes. Maximum remaining: ${remainingHours} hours`;
      errors.push(errorMsg);
      constraints.minutesPerMonth = {
        isAllowed: false,
        reason: errorMsg,
        constraint: "maxMinutesPerMonth",
        remaining: remainingMinutes
      };
    } else {
      constraints.minutesPerMonth = {
        isAllowed: true,
        remaining: remainingMinutes
      };
    }
  }
  if (membership.maxActiveMinutes !== null) {
    const totalActiveMinutes = usageSummary.activeBookingsTotalMinutes + bookingDurationMinutes;
    const remainingMinutes = Math.max(0, membership.maxActiveMinutes - usageSummary.activeBookingsTotalMinutes);
    if (totalActiveMinutes > membership.maxActiveMinutes) {
      const remainingHours = hoursText2(remainingMinutes);
      const errorMsg = `Maximum active minutes (${membership.maxActiveMinutes} min = ${hoursText2(membership.maxActiveMinutes)}h) exceeded. You currently have ${usageSummary.activeBookingsTotalMinutes} minutes of active bookings, this booking would add ${bookingDurationMinutes} minutes. Maximum remaining: ${remainingHours} hours`;
      errors.push(errorMsg);
      constraints.activeMinutes = {
        isAllowed: false,
        reason: errorMsg,
        constraint: "maxActiveMinutes",
        remaining: remainingMinutes
      };
    } else {
      constraints.activeMinutes = {
        isAllowed: true,
        remaining: remainingMinutes
      };
    }
  }
  if (membership.maxActiveSessions != null && membership.remainingActiveSessions != null) {
    const slotsHeld = Math.max(
      0,
      membership.maxActiveSessions - membership.remainingActiveSessions
    );
    const check = checkActiveBookingLimit(slotsHeld, membership.maxActiveSessions);
    if (!check.isAllowed && check.reason && !ACTIVE_SESSION_OVERAGE_ENABLED) ;
    constraints.activeSessions = check;
  }
  if (membership.customBookingHoursEnabled && membership.customBookingHours) {
    const isWithinHours = isWithinCustomBookingHours(
      requestedSlot,
      membership.customBookingHours,
      timezone,
      bookingDate
    );
    if (!isWithinHours.isWithin) {
      const errorMsg = isWithinHours.reason || "Booking is outside allowed hours";
      errors.push(errorMsg);
      constraints.customHours = {
        isAllowed: false,
        reason: errorMsg,
        constraint: "customBookingHours"
      };
    } else {
      constraints.customHours = {
        isAllowed: true
      };
    }
  }
  if (membership.maxMinutesPerWeek !== null) {
    const usagePercent = usageSummary.minutesUsedThisWeek / membership.maxMinutesPerWeek * 100;
    if (usagePercent >= 80 && usagePercent < 100) {
      const hoursUsed = hoursText2(usageSummary.minutesUsedThisWeek);
      const hoursMax = hoursText2(membership.maxMinutesPerWeek);
      warnings.push(
        `You've used ${Math.round(usagePercent)}% of your weekly hours (${hoursUsed} of ${hoursMax} hours)`
      );
    }
  }
  if (membership.maxMinutesPerMonth !== null) {
    const usagePercent = usageSummary.minutesUsedThisMonth / membership.maxMinutesPerMonth * 100;
    if (usagePercent >= 80 && usagePercent < 100) {
      const hoursUsed = hoursText2(usageSummary.minutesUsedThisMonth);
      const hoursMax = hoursText2(membership.maxMinutesPerMonth);
      warnings.push(
        `You've used ${Math.round(usagePercent)}% of your monthly hours (${hoursUsed} of ${hoursMax} hours)`
      );
    }
  }
  if (membership.maxMinutesPerDay !== null) {
    const usagePercent = usageSummary.minutesUsedToday / membership.maxMinutesPerDay * 100;
    if (usagePercent >= 80 && usagePercent < 100) {
      const hoursUsed = hoursText2(usageSummary.minutesUsedToday);
      const hoursMax = hoursText2(membership.maxMinutesPerDay);
      warnings.push(
        `You've used ${Math.round(usagePercent)}% of your daily hours (${hoursUsed} of ${hoursMax} hours)`
      );
    }
  }
  return {
    isValid: true,
    canBook: errors.length === 0,
    errors,
    warnings,
    constraints
  };
}
function validatePassConstraints(pass, requestedSlot, usageSummary, timezone, bookingDate, errors, warnings, constraints) {
  const bookingDurationMinutes = requestedSlot.durationMinutes;
  if (pass.maxMinutesPerDay !== null) {
    const totalMinutesToday = usageSummary.minutesUsedToday + bookingDurationMinutes;
    const remainingMinutes = Math.max(0, pass.maxMinutesPerDay - usageSummary.minutesUsedToday);
    if (totalMinutesToday > pass.maxMinutesPerDay) {
      const bookingDateStr = startOfDay(bookingDate).toLocaleDateString();
      const remainingHours = hoursText2(remainingMinutes);
      const errorMsg = `Maximum minutes per day (${pass.maxMinutesPerDay} min = ${hoursText2(pass.maxMinutesPerDay)}h) exceeded. You have ${usageSummary.minutesUsedToday} minutes booked on ${bookingDateStr}, this booking would add ${bookingDurationMinutes} minutes. Maximum remaining: ${remainingHours} hours`;
      errors.push(errorMsg);
      constraints.minutesPerDay = {
        isAllowed: false,
        reason: errorMsg,
        constraint: "maxMinutesPerDay",
        remaining: remainingMinutes
      };
    } else {
      constraints.minutesPerDay = {
        isAllowed: true,
        remaining: remainingMinutes
      };
    }
  }
  if (pass.maxMinutesPerWeek !== null) {
    const totalMinutesThisWeek = usageSummary.minutesUsedThisWeek + bookingDurationMinutes;
    const remainingMinutes = Math.max(0, pass.maxMinutesPerWeek - usageSummary.minutesUsedThisWeek);
    if (totalMinutesThisWeek > pass.maxMinutesPerWeek) {
      const remainingHours = hoursText2(remainingMinutes);
      const errorMsg = `Maximum minutes per week (${pass.maxMinutesPerWeek} min = ${hoursText2(pass.maxMinutesPerWeek)}h) exceeded. You have ${usageSummary.minutesUsedThisWeek} minutes booked this week, this booking would add ${bookingDurationMinutes} minutes. Maximum remaining: ${remainingHours} hours`;
      errors.push(errorMsg);
      constraints.minutesPerWeek = {
        isAllowed: false,
        reason: errorMsg,
        constraint: "maxMinutesPerWeek",
        remaining: remainingMinutes
      };
    } else {
      constraints.minutesPerWeek = {
        isAllowed: true,
        remaining: remainingMinutes
      };
    }
  }
  if (pass.maxMinutesPerMonth !== null) {
    const totalMinutesThisMonth = usageSummary.minutesUsedThisMonth + bookingDurationMinutes;
    const remainingMinutes = Math.max(0, pass.maxMinutesPerMonth - usageSummary.minutesUsedThisMonth);
    if (totalMinutesThisMonth > pass.maxMinutesPerMonth) {
      const remainingHours = hoursText2(remainingMinutes);
      const errorMsg = `Maximum minutes per month (${pass.maxMinutesPerMonth} min = ${hoursText2(pass.maxMinutesPerMonth)}h) exceeded. You have ${usageSummary.minutesUsedThisMonth} minutes booked this month, this booking would add ${bookingDurationMinutes} minutes. Maximum remaining: ${remainingHours} hours`;
      errors.push(errorMsg);
      constraints.minutesPerMonth = {
        isAllowed: false,
        reason: errorMsg,
        constraint: "maxMinutesPerMonth",
        remaining: remainingMinutes
      };
    } else {
      constraints.minutesPerMonth = {
        isAllowed: true,
        remaining: remainingMinutes
      };
    }
  }
  if (pass.maxActiveMinutes !== null) {
    const totalActiveMinutes = usageSummary.activeBookingsTotalMinutes + bookingDurationMinutes;
    const remainingMinutes = Math.max(0, pass.maxActiveMinutes - usageSummary.activeBookingsTotalMinutes);
    if (totalActiveMinutes > pass.maxActiveMinutes) {
      const remainingHours = hoursText2(remainingMinutes);
      const errorMsg = `Maximum active minutes (${pass.maxActiveMinutes} min = ${hoursText2(pass.maxActiveMinutes)}h) exceeded. You currently have ${usageSummary.activeBookingsTotalMinutes} minutes of active bookings, this booking would add ${bookingDurationMinutes} minutes. Maximum remaining: ${remainingHours} hours`;
      errors.push(errorMsg);
      constraints.activeMinutes = {
        isAllowed: false,
        reason: errorMsg,
        constraint: "maxActiveMinutes",
        remaining: remainingMinutes
      };
    } else {
      constraints.activeMinutes = {
        isAllowed: true,
        remaining: remainingMinutes
      };
    }
  }
  if (pass.maxActiveSessions != null && pass.remainingActiveSessions != null) {
    const slotsHeld = Math.max(
      0,
      pass.maxActiveSessions - pass.remainingActiveSessions
    );
    const check = checkActiveBookingLimit(slotsHeld, pass.maxActiveSessions);
    if (!check.isAllowed && check.reason && !ACTIVE_SESSION_OVERAGE_ENABLED) ;
    constraints.activeSessions = check;
  }
  if (pass.remainingMinutes !== null) {
    if (pass.remainingMinutes <= 0) {
      const hoursTotal = pass.maxTotalMinutes ? hoursText2(pass.maxTotalMinutes) : "unknown";
      const errorMsg = `Pass "${pass.passName}" is fully utilized. You have used all ${hoursTotal} hours.`;
      errors.push(errorMsg);
      constraints.remainingMinutes = {
        isAllowed: false,
        reason: errorMsg,
        constraint: "remainingMinutes",
        remaining: 0
      };
    } else {
      constraints.remainingMinutes = {
        isAllowed: true,
        remaining: pass.remainingMinutes
      };
    }
  } else {
    constraints.remainingMinutes = {
      isAllowed: true
    };
  }
  if (pass.customBookingHoursEnabled && pass.customBookingHours) {
    const isWithinHours = isWithinCustomBookingHours(
      requestedSlot,
      pass.customBookingHours,
      timezone,
      bookingDate,
      "pass"
    );
    if (!isWithinHours.isWithin) {
      const errorMsg = isWithinHours.reason || "Booking is outside allowed hours";
      errors.push(errorMsg);
      constraints.customHours = {
        isAllowed: false,
        reason: errorMsg,
        constraint: "customBookingHours"
      };
    } else {
      constraints.customHours = {
        isAllowed: true
      };
    }
  }
  if (pass.maxMinutesPerWeek !== null) {
    const usagePercent = usageSummary.minutesUsedThisWeek / pass.maxMinutesPerWeek * 100;
    if (usagePercent >= 80 && usagePercent < 100) {
      const hoursUsed = hoursText2(usageSummary.minutesUsedThisWeek);
      const hoursMax = hoursText2(pass.maxMinutesPerWeek);
      warnings.push(
        `You've used ${Math.round(usagePercent)}% of your weekly hours (${hoursUsed} of ${hoursMax} hours)`
      );
    }
  }
  if (pass.maxMinutesPerMonth !== null) {
    const usagePercent = usageSummary.minutesUsedThisMonth / pass.maxMinutesPerMonth * 100;
    if (usagePercent >= 80 && usagePercent < 100) {
      const hoursUsed = hoursText2(usageSummary.minutesUsedThisMonth);
      const hoursMax = hoursText2(pass.maxMinutesPerMonth);
      warnings.push(
        `You've used ${Math.round(usagePercent)}% of your monthly hours (${hoursUsed} of ${hoursMax} hours)`
      );
    }
  }
  if (pass.maxTotalMinutes !== null && pass.remainingMinutes !== null) {
    const minutesUsed = pass.maxTotalMinutes - pass.remainingMinutes;
    const usagePercent = minutesUsed / pass.maxTotalMinutes * 100;
    if (usagePercent >= 80 && usagePercent < 100) {
      const hoursRemaining = hoursText2(pass.remainingMinutes);
      warnings.push(
        `Only ${hoursRemaining} hours remaining on your pass`
      );
    }
  }
  return {
    isValid: true,
    canBook: errors.length === 0,
    errors,
    warnings,
    constraints
  };
}

// src/subscription/calculators.ts
var RECURRING_FREQUENCIES = [
  "monthly",
  "quarterly",
  "semi-annually",
  "annually"
];
function isRecurringBillingFrequency(value) {
  return RECURRING_FREQUENCIES.includes(value);
}
function getMonthIncrement(billingFrequency) {
  if (billingFrequency === "quarterly") return 3;
  if (billingFrequency === "semi-annually") return 6;
  if (billingFrequency === "annually") return 12;
  return 1;
}
var DEFAULT_CALENDAR = "utc";
function resolveCalendar(options) {
  return options.calendar ?? DEFAULT_CALENDAR;
}
function addMonthsClamped(date, months) {
  const result = new Date(date);
  const originalDay = result.getDate();
  result.setDate(1);
  result.setMonth(result.getMonth() + months);
  const daysInTarget = new Date(result.getFullYear(), result.getMonth() + 1, 0).getDate();
  result.setDate(Math.min(originalDay, daysInTarget));
  return result;
}
function addMonthsClampedUtc(date, months) {
  const year = date.getUTCFullYear();
  const month = date.getUTCMonth() + months;
  const day = date.getUTCDate();
  const daysInTarget = new Date(Date.UTC(year, month + 1, 0)).getUTCDate();
  return new Date(Date.UTC(year, month, Math.min(day, daysInTarget), date.getUTCHours(), date.getUTCMinutes(), date.getUTCSeconds(), date.getUTCMilliseconds()));
}
function stepMonths(date, months, calendar) {
  return calendar === "utc" ? addMonthsClampedUtc(date, months) : addMonthsClamped(date, months);
}
function getBillingCycleDates(startDate, currentDate, billingFrequency = "monthly", options = {}) {
  const monthIncrement = getMonthIncrement(billingFrequency);
  const calendar = resolveCalendar(options);
  let cycleStart = new Date(startDate);
  while (true) {
    const nextCycleStart2 = stepMonths(cycleStart, monthIncrement, calendar);
    if (currentDate < nextCycleStart2) break;
    cycleStart = nextCycleStart2;
  }
  const nextCycleStart = stepMonths(cycleStart, monthIncrement, calendar);
  const cycleEnd = new Date(nextCycleStart);
  if (calendar === "utc") cycleEnd.setUTCDate(cycleEnd.getUTCDate() - 1);
  else cycleEnd.setDate(cycleEnd.getDate() - 1);
  return { cycleStart, cycleEnd };
}
function calculateUsagePercentage(used, max) {
  if (max === null || max === 0) {
    return null;
  }
  return Math.min(100, used / max * 100);
}
function getSubscriptionExpirationDate(subscription) {
  if (subscription.type === "none") {
    return null;
  }
  if (subscription.type === "membership") {
    return subscription.endDate;
  }
  if (subscription.type === "pass") {
    return subscription.expirationDate;
  }
  return null;
}
function getSubscriptionStatus(subscription, currentDate = /* @__PURE__ */ new Date()) {
  if (subscription.type === "none") {
    return "none";
  }
  if (subscription.type === "membership") {
    if (subscription.ended) {
      return "cancelled";
    }
    if (subscription.endDate && subscription.endDate < currentDate) {
      return "expired";
    }
    return "active";
  }
  if (subscription.type === "pass") {
    return subscription.status;
  }
  return "none";
}
function getNextRenewalDate(startDate, currentDate, billingFrequency = "monthly", options = {}) {
  const { cycleEnd } = getBillingCycleDates(startDate, currentDate, billingFrequency, options);
  const renewalDate = new Date(cycleEnd);
  if (resolveCalendar(options) === "utc") renewalDate.setUTCDate(renewalDate.getUTCDate() + 1);
  else renewalDate.setDate(renewalDate.getDate() + 1);
  return renewalDate;
}

// src/subscription/election.ts
var at = (d) => (d instanceof Date ? d : new Date(d)).getTime();
var successorFirst = (a, b) => {
  if (a.kind === "pass" || b.kind === "pass") return 0;
  return at(b.startDate) - at(a.startDate) || b.id - a.id;
};
var oldestFirst = (a, b) => {
  if (a.kind !== "pass" || b.kind !== "pass") return 0;
  return at(a.purchaseDate) - at(b.purchaseDate) || a.id - b.id;
};
var PLAN_ELECTION = [
  { kind: "team-membership", electable: ["active"], order: successorFirst },
  { kind: "personal-membership", electable: ["active"], order: successorFirst },
  { kind: "pass", electable: ["active"], order: oldestFirst }
];
function electableFor(row, candidates) {
  return candidates.filter((c) => c.kind === row.kind && row.electable.includes(c.lifecycle)).sort(row.order);
}
function electPlan(candidates) {
  for (const row of PLAN_ELECTION) {
    const [winner] = electableFor(row, candidates);
    if (winner) return winner;
  }
  return null;
}

// src/subscription/payer.ts
var at2 = (d) => (d instanceof Date ? d : new Date(d)).getTime();
function passIsSpendable(row, booking, now) {
  if (row.status !== "active") return false;
  if (row.expiresAt !== null) {
    const expires = at2(row.expiresAt);
    if (expires <= now.getTime() || expires < at2(booking.endsAt)) return false;
  }
  if (row.remainingMinutes === null) return row.effectiveCapMinutes === null;
  return row.remainingMinutes >= booking.durationMinutes;
}
function electPayer(input) {
  if (input.identityKind !== "personal-membership") {
    return { payer: "identity", reason: "identity-not-personal-membership" };
  }
  const covering = input.passCandidates.filter((c) => passIsSpendable(c, input.booking, input.now)).filter((c) => c.poolsAdmit && c.withinAdvanceWindow && c.withinPassHours).map((c) => ({ kind: "pass", id: c.id, purchaseDate: c.purchaseDate, lifecycle: c.status })).sort(oldestFirst);
  const [winner] = covering;
  return winner ? { payer: "pass", id: winner.id } : { payer: "identity", reason: "no-covering-pass" };
}
var cardFor = (holdings, type, id) => {
  if (!holdings) return null;
  const list = type === "membership" ? holdings.memberships : holdings.passes;
  return list.find((c) => c.id === id) ?? null;
};
function payerFromQuote(input) {
  const { quoteSubscription, holdings, elected } = input;
  if (quoteSubscription === null) return null;
  if (quoteSubscription === void 0) {
    if (!elected) return null;
    const card2 = cardFor(holdings, elected.type, elected.id);
    return { type: elected.type, id: elected.id, name: elected.plan?.name ?? card2?.plan?.name ?? null, card: card2 };
  }
  const card = cardFor(holdings, quoteSubscription.type, quoteSubscription.id);
  return {
    type: quoteSubscription.type,
    id: quoteSubscription.id,
    name: quoteSubscription.name ?? card?.plan?.name ?? null,
    card
  };
}

// src/subscription/holding.ts
var packLifecycle = (status) => status === "scheduled" ? "scheduled" : status === "used" ? "used-with-minutes" : "active";
var HOLDING_MATRIX = [
  { candidate: "personal-membership", refusedBy: "team-seat" },
  { candidate: "personal-membership", refusedBy: "personal-membership", unlessQueueing: true },
  { candidate: "pass", refusedBy: "pass" },
  { candidate: "pass", refusedBy: "team-membership" },
  { candidate: "pass", refusedBy: "personal-membership" },
  { candidate: "lesson-pack", refusedBy: "lesson-pack" }
];
var REFUSAL = {
  "team-seat": "held_team_seat",
  "team-membership": "held_team_membership",
  "personal-membership": "held_personal_membership",
  pass: "held_pass",
  "lesson-pack": "held_lesson_pack"
};
function refusingKinds(candidate, opts = {}) {
  return HOLDING_MATRIX.filter((row) => row.candidate === candidate && !(row.unlessQueueing && opts.queueing)).map((row) => row.refusedBy);
}
function canHold(candidate, held, opts = {}) {
  for (const kind of refusingKinds(candidate, opts)) {
    const offenders = held.filter((h) => h.kind === kind);
    if (offenders.length > 0) return { allowed: false, reason: REFUSAL[kind], held: offenders };
  }
  return { allowed: true };
}

// src/subscription/lifecycle.ts
var at3 = (d) => (d instanceof Date ? d : new Date(d)).getTime();
function membershipLifecycle(row, now, startCutoff) {
  if (row.ended || row.startDate === null) return "ended";
  const start = at3(row.startDate);
  if (start >= startCutoff.getTime()) return "upcoming";
  if (row.endDate !== null && at3(row.endDate) <= now.getTime()) return "ended";
  return "active";
}

// src/subscription/heldFromPayload.ts
function heldFromPayload(holdings) {
  const held = [];
  if (holdings.teamSeat) {
    held.push({ kind: "team-seat", id: holdings.teamSeat.teamId, templateId: null, lifecycle: "active" });
  }
  for (const card of holdings.memberships) {
    held.push({
      kind: card.teamId !== null ? "team-membership" : "personal-membership",
      id: card.id,
      templateId: card.membershipId,
      lifecycle: card.lifecycle
    });
  }
  for (const card of holdings.passes) {
    held.push({ kind: "pass", id: card.id, templateId: card.passId, lifecycle: card.lifecycle });
  }
  for (const card of holdings.lessonPacks) {
    held.push({ kind: "lesson-pack", id: card.id, templateId: card.lessonId, lifecycle: packLifecycle(card.status) });
  }
  return held;
}

// src/subscription/refusalCopy.ts
var HOLDING_REFUSAL_CODES = [
  "duplicate_active_membership",
  "team_user_already_on_team",
  "duplicate_active_pass",
  "pass_blocked_by_membership"
];
function isHoldingRefusalCode(code) {
  return typeof code === "string" && HOLDING_REFUSAL_CODES.includes(code);
}
function holdingRefusalCopy(input) {
  const { candidate, reason, audience } = input;
  const admin = audience === "admin";
  if (candidate === "personal-membership") {
    switch (reason) {
      case "held_personal_membership":
        return admin ? "This member already has a membership. End it before starting a new one." : "You already have a membership. Contact the club to change your plan.";
      case "held_team_seat": {
        const teamName = input.teamName;
        if (!teamName) throw new Error("holdingRefusalCopy: a team-seat refusal needs the team name");
        return admin ? `This member is on team "${teamName}". Remove them from the team before assigning a personal membership.` : `You're on team "${teamName}" at this club. Contact the club to change your plan.`;
      }
    }
  }
  if (candidate === "pass") {
    switch (reason) {
      case "held_pass":
        return admin ? "This member already has an active pass. Cancel it before adding another." : "You already have an active pass. Contact the club to change your plan.";
      case "held_team_membership":
      case "held_personal_membership":
        return admin ? "This member already has a membership. End it before assigning a pass." : "You already have a membership. Contact the club to change your plan.";
    }
  }
  throw new Error(`holdingRefusalCopy: no copy for ${candidate} refused by ${reason}`);
}

// src/subscription/formatters.ts
function formatUsageSummary(summary) {
  const parts = [];
  const hoursToday = formatHours(summary.minutesUsedToday);
  parts.push(
    `Today: ${hoursToday} ${pluralize("hour", Number(hoursToday))} (${summary.visitsToday} ${pluralize("visit", summary.visitsToday)})`
  );
  const hoursThisWeek = formatHours(summary.minutesUsedThisWeek);
  parts.push(
    `This Week: ${hoursThisWeek} ${pluralize("hour", Number(hoursThisWeek))} (${summary.visitsThisWeek} ${pluralize("visit", summary.visitsThisWeek)})`
  );
  const hoursThisMonth = formatHours(summary.minutesUsedThisMonth);
  parts.push(
    `This Month: ${hoursThisMonth} ${pluralize("hour", Number(hoursThisMonth))} (${summary.visitsThisMonth} ${pluralize("visit", summary.visitsThisMonth)})`
  );
  const activeHours = formatHours(summary.activeBookingsTotalMinutes);
  parts.push(
    `Active: ${summary.activeBookingsCount} ${pluralize("booking", summary.activeBookingsCount)} (${activeHours} ${pluralize("hour", Number(activeHours))})`
  );
  if (summary.totalVisitsUsed !== void 0) {
    parts.push(`Total Visits Used: ${summary.totalVisitsUsed}`);
  }
  return parts.join(" | ");
}
function formatConstraintCheck(check) {
  const icon = check.isAllowed ? "\u2705" : "\u274C";
  const parts = [icon];
  if (check.reason) {
    parts.push(check.reason);
  } else if (check.isAllowed) {
    parts.push("Allowed");
  } else {
    parts.push("Not allowed");
  }
  if (check.remaining !== void 0 && check.remaining !== null) {
    parts.push(`(${check.remaining} remaining)`);
  }
  if (check.suggestion) {
    parts.push(`${check.suggestion}`);
  }
  return parts.join(" ");
}
function formatValidationResult(validation) {
  const lines = [];
  if (validation.isValid && validation.canBook) {
    lines.push("\u2705 Booking Allowed");
  } else if (!validation.isValid) {
    lines.push("\u274C Subscription Invalid");
  } else {
    lines.push("\u274C Booking Not Allowed");
  }
  lines.push("");
  if (validation.errors.length > 0) {
    lines.push("Errors:");
    validation.errors.forEach((error) => {
      lines.push(`  \u2022 ${error}`);
    });
    lines.push("");
  }
  if (validation.warnings.length > 0) {
    lines.push("Warnings:");
    validation.warnings.forEach((warning) => {
      lines.push(`  \u26A0\uFE0F  ${warning}`);
    });
    lines.push("");
  }
  const constraintEntries = Object.entries(validation.constraints);
  if (constraintEntries.length > 0) {
    lines.push("Constraint Details:");
    constraintEntries.forEach(([name, check]) => {
      if (check) {
        const label = formatConstraintName(name);
        const status = formatConstraintCheck(check);
        lines.push(`  ${label}: ${status}`);
      }
    });
  }
  return lines.join("\n");
}
function formatHours(minutes) {
  const hours = minutes / 60;
  const rounded = Math.round(hours * 10) / 10;
  return Number.isInteger(rounded) ? String(rounded) : rounded.toFixed(1);
}
function pluralize(word, count) {
  if (count === 1) {
    return word;
  }
  if (word.endsWith("y")) {
    return word.slice(0, -1) + "ies";
  }
  if (word.endsWith("s") || word.endsWith("x") || word.endsWith("ch")) {
    return word + "es";
  }
  return word + "s";
}
function formatConstraintName(name) {
  const nameMap = {
    hoursPerDay: "Daily Hours",
    hoursPerMonth: "Monthly Hours",
    activeHours: "Active Hours",
    customHours: "Custom Hours",
    expirationDate: "Expiration",
    remainingVisits: "Remaining Visits"
  };
  return nameMap[name] || name;
}

export { ACTIVE_SESSION_OVERAGE_ENABLED, HOLDING_MATRIX, HOLDING_REFUSAL_CODES, INACTIVE_AFTER_DAYS, PLAN_ELECTION, addMonthsClamped, addMonthsClampedUtc, calculateUsagePercentage, canBookAtTime, canHold, checkActiveBookingLimit, checkActiveMinutesLimit, checkDailyMinuteLimit, checkDailyVisitLimit, checkMonthlyMinuteLimit, checkPassRemainingMinutes, checkWeeklyMinuteLimit, electPayer, electPlan, electableFor, formatConstraintCheck, formatUsageSummary, formatValidationResult, getBillingCycleDates, getNextRenewalDate, getSubscriptionExpirationDate, getSubscriptionStatus, hasActivePlan, heldFromPayload, holdingRefusalCopy, isHoldingRefusalCode, isMembershipBooker, isNoSubscription, isPassBooker, isRecurringBillingFrequency, isSubscriptionValid, isWithinCustomBookingHours, membershipLifecycle, oldestFirst, packLifecycle, passIsSpendable, payerFromQuote, refusingKinds, successorFirst, validateSubscriptionForBooking };
//# sourceMappingURL=index.js.map
//# sourceMappingURL=index.js.map